import React, { useEffect, useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import LeaveRequestPDF from './LeaveRequestPDF';
import { getLeaveById } from '../services/leaves';
import { Button, CircularProgress } from '@mui/material';

/**
 * LeavePDFViewer
 * Fetches a leave by ID using backend GET /api/leaves/:leaveId and renders a download link for the PDF.
 *
 * Props:
 * - leaveId: number|string (required)
 * - entitlementDays: number (optional, used when leave.type !== 'annuel')
 * - fileName: string (optional)
 * - variant/color/size: MUI Button props passthrough (optional)
 */
const LeavePDFViewer = ({ leaveId, entitlementDays, fileName = 'demande-conge.pdf', variant = 'contained', color = 'primary', size = 'small' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!leaveId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getLeaveById(leaveId);
        if (!mounted) return;
        const leave = res.data || {};
        const employee = leave.employee || {};
        setData({
          leave,
          employee,
          entitlement_days: entitlementDays,
        });
      } catch (e) {
        if (!mounted) return;
        if (e.response?.status === 500) {
          setError('Erreur serveur (500) - Vérifiez les logs du backend');
        } else if (e.response?.status === 404) {
          setError('Demande introuvable');
        } else if (e.response?.status === 401) {
          setError('Non autorisé - Reconnectez-vous');
        } else {
          setError(`Chargement échoué: ${e.message || 'Erreur inconnue'}`);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [leaveId, entitlementDays]);

  if (!leaveId) return null;

  if (loading) {
    return (
      <Button variant={variant} color={color} size={size} disabled startIcon={<CircularProgress size={16} />}>
        Préparation du PDF…
      </Button>
    );
  }

  if (error) {
    return (
      <Button variant={variant} color="error" size={size} disabled>
        {error}
      </Button>
    );
  }

  if (!data) return null;

  return (
    <PDFDownloadLink
      document={<LeaveRequestPDF data={data} />}
      fileName={fileName}
    >
      {({ loading: genLoading }) => (
        // Commented out per request:
        // <Button variant={variant} color={color} size={size} disabled={genLoading}>
        //   {genLoading ? 'Génération…' : 'Télécharger la demande (PDF)'}
        // </Button>
        null
      )}
    </PDFDownloadLink>
  );
};

export default LeavePDFViewer;
